import type { FaceKey } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { FACE_LABELS } from '../face-labels';
import type { ViewableFace } from '../viewable-faces';

interface FaceTabsProps {
  readonly faces: readonly ViewableFace[];
  readonly value: FaceKey;
  readonly onChange: (face: FaceKey) => void;
}

const TAB_BASE =
  'flex h-12 cursor-pointer items-center gap-1.5 border-b-2 text-[15px] whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const TAB_SELECTED = 'border-foreground font-bold text-foreground';
const TAB_IDLE = 'border-transparent text-muted-foreground hover:text-foreground';

/**
 * 면 탭(뷰어 설계 V11) — 편집기 면 전환(FaceSwitcher)과 같은 밑줄 탭. 편집영역에 있는 면만 앞면·뒷면·왼쪽·오른쪽 순서로 둔다.
 * 디자인이 있는 면은 글자 옆 라임 점. 좁은 화면에서는 탭 줄을 가로로 민다.
 */
export const FaceTabs = ({ faces, value, onChange }: FaceTabsProps): ReactElement => (
  <nav aria-label="인쇄 면" className="min-w-0 overflow-x-auto [scrollbar-width:none]">
    <ul className="flex w-max gap-6 sm:gap-8">
      {faces.map((option) => {
        const isSelected = option.face === value;
        return (
          <li key={option.face} className="-mb-px shrink-0">
            <button
              type="button"
              aria-pressed={isSelected}
              aria-label={`${FACE_LABELS[option.face]}${option.hasLayers ? ' (디자인 있음)' : ''}`}
              onClick={() => onChange(option.face)}
              className={`${TAB_BASE} ${isSelected ? TAB_SELECTED : TAB_IDLE}`}
            >
              {FACE_LABELS[option.face]}
              {option.hasLayers ? (
                <span
                  aria-hidden
                  className="size-1.5 rounded-full bg-brand-lime ring-1 ring-foreground/40"
                />
              ) : null}
            </button>
          </li>
        );
      })}
    </ul>
  </nav>
);
